import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, PaperPlaneRight, Spinner, Paperclip, Plus, Chat,
  Clock, Trash, X, Check, CaretLeft, CaretRight, File, CheckCircle, XCircle,
  CalendarBlank, UserCircle, MapPin, CalendarPlus,
  Heart, Eye, Notebook, Target, Warning, SunHorizon, ListChecks,
  MagnifyingGlass, Lightning, CaretDown, ThumbsUp, ThumbsDown,
  SpeakerHigh, SpeakerSlash,
} from '@phosphor-icons/react';
import { MainLayout } from '../components/shared/MainLayout';
import { useAuth } from '../context/AuthContext';
import { trackOnce, track, ACTIVATION_EVENTS } from '../services/activation';
import { useChatExitNoSend } from '../hooks/useFrictionEvents';
import AppointmentPasteDetector, { looksLikeAppointment } from '../components/chat/AppointmentPasteDetector';
import ChatContextBanner from '../components/chat/ChatContextBanner';
import ChatStarterPrompts from '../components/chat/ChatStarterPrompts';
import SourceTrustStrip from '../components/chat/SourceTrustStrip';
import ReplyActionChips from '../components/chat/ReplyActionChips';
import VoiceInputButton from '../components/voice/VoiceInputButton';
import VoicePlayButton from '../components/voice/VoicePlayButton';
import VoiceWelcomeCard from '../components/voice/VoiceWelcomeCard';
import StreamingProgress from '../components/chat/StreamingProgress';

/* ─── Health Action Cards ─── */
const HealthActionCard = ({ item, onConfirm, onDismiss, confirming }) => (
  <div className="bg-white border border-amber-200/60 rounded-lg p-2.5 flex items-center justify-between" data-testid="health-action-card">
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-amber-800 truncate">{item.label}</p>
      {item.confidence === 'low' && <p className="text-[10px] text-amber-500">Low confidence</p>}
    </div>
    <div className="flex gap-1 ml-2">
      <button onClick={() => onConfirm(item.id)} disabled={confirming === item.id}
        className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-50"
        data-testid="confirm-fact-btn" title="Save to health record">
        {confirming === item.id ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
      </button>
      <button onClick={() => onDismiss(item.id)} disabled={confirming === item.id}
        className="p-1.5 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors disabled:opacity-50"
        data-testid="dismiss-fact-btn" title="Dismiss">
        <X size={14} />
      </button>
    </div>
  </div>
);

const AppointmentActionCard = ({ item, onConfirm, onDismiss, confirming }) => {
  const d = item.data || {};
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5" data-testid="appointment-action-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarBlank size={14} className="text-blue-600" weight="duotone" />
          <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Appointment found</span>
        </div>
        <span className="text-[9px] text-blue-400">{item.confidence} confidence</span>
      </div>
      <p className="text-sm font-medium text-slate-800 mb-1.5">{d.title || item.label}</p>
      <div className="space-y-1 mb-3">
        {d.date && <div className="flex items-center gap-1.5 text-[11px] text-slate-600"><CalendarBlank size={11} className="text-slate-400" /><span>{d.date}{d.time ? ` at ${d.time}` : ''}</span></div>}
        {d.clinician_name && <div className="flex items-center gap-1.5 text-[11px] text-slate-600"><UserCircle size={11} className="text-slate-400" /><span>{d.clinician_name}{d.specialty ? ` (${d.specialty})` : ''}</span></div>}
        {d.clinic_name && <div className="flex items-center gap-1.5 text-[11px] text-slate-600"><MapPin size={11} className="text-slate-400" /><span>{d.clinic_name}</span></div>}
        {d.notes && <p className="text-[10px] text-slate-500 mt-1 italic">{d.notes}</p>}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onConfirm(item.id)} disabled={confirming === item.id}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          data-testid="confirm-appointment-btn">
          {confirming === item.id ? <Spinner size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
          Add to appointments
        </button>
        <button onClick={() => onDismiss(item.id)} disabled={confirming === item.id}
          className="px-3 py-2 rounded-lg bg-slate-100 text-slate-500 text-xs hover:bg-slate-200 transition-colors disabled:opacity-50"
          data-testid="dismiss-appointment-btn">
          Skip
        </button>
      </div>
    </div>
  );
};

/* ─── Chat Message Bubble ─── */
const MessageBubble = React.memo(({ msg, onConfirm, onDismiss, confirming, sessionId, hasAppointmentContext, onFollowUpFocus, onSendFollowup, autoVoice = false }) => {
  const isUser = msg.role === 'user';
  const pending = msg.health_actions?.pending_confirmation || [];
  const isReportProcessing = msg.report_processing?.status === 'processing';
  // Parse optional reasoning footer from assistant replies — strips the §§…§§ metadata
  // from what the user sees and surfaces it as subtle chips below the bubble.
  // While streaming, we don't parse (the footer hasn't arrived yet), so chips only
  // appear once the reply is complete.
  const { body: displayContent, chips } = (!isUser && !msg.streaming)
    ? parseReasoningFooter(msg.content)
    : { body: msg.content, chips: null };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-5`}>
      <div className={`max-w-[85%] ${isUser ? 'order-1' : 'order-0'}`}>
        {msg.attachment && (() => {
          // Wave 3 fix sprint (2026-02) — render a real preview card for chat
          // attachments instead of just the filename. Image attachments show a
          // thumbnail (capped at 240×240, contained, rounded). Non-image
          // attachments show a document-style card with filename + type + size.
          // For optimistic temp messages we use the local objectURL; for
          // persisted messages refetched from the server we hit the
          // /chat/attachments/{file_id} endpoint.
          const a = msg.attachment;
          const isImageType = (a.type || a.file_type || '').startsWith('image/');
          const previewSrc = a.previewUrl || (isImageType && a.file_id ? `${API}/chat/attachments/${a.file_id}` : null);
          const isImage = isImageType && previewSrc;
          const fmtSize = (n) => {
            if (!n) return null;
            if (n < 1024) return `${n} B`;
            if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
            return `${(n / (1024 * 1024)).toFixed(1)} MB`;
          };
          const sizeLabel = fmtSize(a.size || a.file_size);
          const typeLabel = (a.type || a.file_type || '').split('/').pop()?.toUpperCase() || 'FILE';
          if (isImage) {
            return (
              <div className={`mb-1.5 ${isUser ? 'flex justify-end' : ''}`} data-testid="chat-attachment-image">
                <a href={previewSrc} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={previewSrc}
                    alt={a.filename}
                    loading="lazy"
                    decoding="async"
                    className="max-w-[240px] max-h-[240px] w-auto h-auto rounded-xl border border-slate-200 bg-[#F4F1EB] object-contain"
                    style={{ maxWidth: '100%' }}
                  />
                </a>
                <div className={`mt-1 text-[10.5px] text-slate-400 truncate max-w-[240px] ${isUser ? 'text-right' : ''}`}>
                  {a.filename}{sizeLabel ? ` · ${sizeLabel}` : ''}
                </div>
              </div>
            );
          }
          return (
            <div className={`mb-1.5 ${isUser ? 'flex justify-end' : ''}`} data-testid="chat-attachment-file">
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 max-w-[260px]">
                <File size={16} className="text-slate-400 flex-shrink-0" weight="duotone" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-medium text-slate-700 truncate">{a.filename}</p>
                  <p className="text-[10.5px] text-slate-400">{typeLabel}{sizeLabel ? ` · ${sizeLabel}` : ''}</p>
                </div>
              </div>
            </div>
          );
        })()}
        {msg.report && (
          <div className={`flex items-center gap-1.5 mb-1 text-xs ${isUser ? 'justify-end' : ''}`}>
            <File size={12} className="text-slate-400" />
            <span className="text-slate-500">{msg.report.files?.length || 0} files uploaded</span>
          </div>
        )}
        <div className={`rounded-2xl ${
          isUser
            ? 'bg-[#235347] text-white rounded-br-md px-4 py-2.5'
            : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md px-4 py-3 sm:px-[18px] sm:py-3.5'
        }`}>
          {isReportProcessing ? (
            <div className="flex items-center gap-2 py-1">
              <Spinner size={14} className="animate-spin text-slate-400" />
              <span className="text-sm text-slate-500">Reviewing your files...</span>
            </div>
          ) : !isUser && msg.streaming && !msg.content ? (
            <StreamingProgress />
          ) : (
            <div className={`text-sm whitespace-pre-wrap ${isUser ? 'leading-relaxed' : 'leading-[1.65] tracking-[0.005em] prose-sm'}`}
              dangerouslySetInnerHTML={!isUser ? { __html: formatMarkdown(displayContent) + (msg.streaming ? '<span class="inline-block w-0.5 h-4 bg-slate-400 ml-0.5 animate-pulse align-text-bottom"></span>' : '') } : undefined}>
              {isUser ? msg.content : undefined}
            </div>
          )}
        </div>
        {/* Reasoning chips — subtle rank / key variable / next step */}
        {!isUser && chips && <ReasoningChips chips={chips} />}
        {/* Extraction suggestions — visually separated from the answer */}
        {!isUser && pending.length > 0 && (
          <div className="mt-2.5 ml-1 pl-3 border-l-2 border-amber-200/50 space-y-1.5" data-testid="health-actions">
            <p className="text-[10px] text-amber-600/70 font-medium uppercase tracking-wider flex items-center gap-1">
              <Lightning size={10} weight="fill" />
              {pending.some(p => p.card_type === 'appointment') ? 'Found — review and confirm' : 'Save to health record?'}
            </p>
            {pending.map((item) => (
              item.card_type === 'appointment' || item.type === 'appointment' ? (
                <AppointmentActionCard key={item.id} item={item} onConfirm={onConfirm} onDismiss={onDismiss} confirming={confirming} />
              ) : (
                <HealthActionCard key={item.id} item={item} onConfirm={onConfirm} onDismiss={onDismiss} confirming={confirming} />
              )
            ))}
          </div>
        )}
        {!isUser && msg.extraction_summary && (
          <div className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-400">
            <File size={10} />
            <span>{msg.extraction_summary.document_type}</span>
            {msg.extraction_summary.conditions_count > 0 && <span>· {msg.extraction_summary.conditions_count} conditions</span>}
            {msg.extraction_summary.medications_count > 0 && <span>· {msg.extraction_summary.medications_count} meds</span>}
          </div>
        )}
        {/* Visible context line — what Kindra actually drew on for this answer.
            Only shows after streaming finishes for assistant messages.
            Stage 4: upgraded to premium source chip strip for stronger trust. */}
        {!isUser && !msg.streaming && msg.using_context && (
          <SourceTrustStrip text={msg.using_context} />
        )}
        {/* Stage 17 P5 — persistent safety disclaimer under every substantive
            assistant reply. Calm, tiny, grey — reads once and fades into
            peripheral trust. Hidden for report-processing system messages. */}
        {!isUser && !msg.streaming && msg.content && msg.content.length > 60 && !isReportProcessing && (
          <p
            className="mt-1.5 text-[10.5px] text-slate-400 leading-snug italic"
            data-testid="chat-safety-disclaimer"
          >
            Kindra can help explain and prepare — it doesn't diagnose, prescribe, or replace your clinician.
          </p>
        )}
        {/* Stage 10 — Voice output. Speaker icon below assistant replies.
            Auto-reads when global "voice mode" is on (persisted in localStorage,
            consulted live each render via the autoPlay prop coming from the
            parent ChatPage). */}
        {!isUser && !msg.streaming && msg.content && !isReportProcessing && (
          <VoicePlayButton text={msg.content} messageId={msg.id} autoPlay={autoVoice && msg.autoVoiceEligible === true} />
        )}
        {/* Stage 9 polish (audit P4): persistent action chips after each
            assistant reply. Only shows once streaming has finished and we
            have a real server-assigned message id. Hides for system /
            confirmation-required action messages so we don't double-CTA. */}
        {!isUser && !msg.streaming && msg.id && msg.content && !isReportProcessing && (
          <ReplyActionChips
            message={msg}
            sessionId={msg.session_id || sessionId}
            hasAppointmentContext={hasAppointmentContext}
            onFollowUpFocus={onFollowUpFocus}
            onSendFollowup={onSendFollowup}
            onToggleSources={() => {/* SourceTrustStrip is always visible — chip click is tracked-only for now */}}
          />
        )}
        {/* Thumbs-up/down for assistant messages — only after streaming finishes,
            only for messages with a real server-assigned id. */}
        {!isUser && !msg.streaming && msg.id && !isReportProcessing && msg.content && (
          <FeedbackChip messageId={msg.id} sessionId={msg.session_id || sessionId} />
        )}
      </div>
    </motion.div>
  );
});

/* ─── Feedback chip (thumbs up / down) ───
    Stage 18 — thumbs-down now opens a tiny optional textarea so the user
    can tell us what felt wrong, unsafe, confusing, or unhelpful.
    The reason is sent alongside the rating to `/api/chat/feedback`
    (backend already supports the `reason` field; it's surfaced in the
    founder dashboard's "recent thumbs-down" feed). Stays optional —
    dismissing or submitting empty still records the down-vote. */
const FeedbackChip = ({ messageId, sessionId }) => {
  const [rating, setRating] = React.useState(null); // 'up' | 'down' | null
  const [submitting, setSubmitting] = React.useState(false);
  const [showReason, setShowReason] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [reasonSent, setReasonSent] = React.useState(false);

  const sendRating = async (r, reasonText) => {
    try {
      await axios.post(`${API}/chat/feedback`, {
        session_id: sessionId || '',
        message_id: messageId,
        rating: r,
        ...(reasonText ? { reason: reasonText } : {}),
      });
      return true;
    } catch {
      return false;
    }
  };

  const onClickRating = async (r) => {
    if (rating === r || submitting) return;
    setSubmitting(true);
    setRating(r); // optimistic
    const ok = await sendRating(r);
    setSubmitting(false);
    if (!ok) { setRating(null); return; }
    if (r === 'down') setShowReason(true); // reveal the reason textarea
  };

  const submitReason = async () => {
    const trimmed = reason.trim();
    if (!trimmed || submitting) { setShowReason(false); return; }
    setSubmitting(true);
    const ok = await sendRating('down', trimmed);
    setSubmitting(false);
    if (ok) {
      setReasonSent(true);
      setTimeout(() => setShowReason(false), 1200);
    }
  };

  const base = 'p-1.5 rounded-full transition-colors text-slate-400 hover:text-[#235347]';
  return (
    <div className="mt-1.5" data-testid={`feedback-row-${messageId}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onClickRating('up')}
          disabled={submitting}
          className={`${base} ${rating === 'up' ? 'text-[#235347] bg-[#235347]/8' : 'hover:bg-slate-100'}`}
          aria-label="Helpful"
          data-testid={`feedback-up-${messageId}`}
        >
          <ThumbsUp size={13} weight={rating === 'up' ? 'fill' : 'regular'} />
        </button>
        <button
          type="button"
          onClick={() => onClickRating('down')}
          disabled={submitting}
          className={`${base} ${rating === 'down' ? 'text-amber-700 bg-amber-50' : 'hover:bg-slate-100'}`}
          aria-label="Not helpful"
          data-testid={`feedback-down-${messageId}`}
        >
          <ThumbsDown size={13} weight={rating === 'down' ? 'fill' : 'regular'} />
        </button>
        {rating && !showReason && (
          <span className="text-[10px] text-slate-400 ml-1">{rating === 'up' ? 'Thanks' : (reasonSent ? 'Noted — thanks' : 'Noted')}</span>
        )}
      </div>
      {showReason && !reasonSent && (
        <div
          className="mt-2 max-w-md rounded-lg border border-amber-200/70 bg-amber-50/40 px-3 py-2.5"
          data-testid={`feedback-reason-${messageId}`}
        >
          <p className="text-[11px] text-amber-900/80 mb-1.5 leading-snug">
            Tell us what felt wrong, unsafe, confusing, or unhelpful (optional).
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            rows={2}
            maxLength={500}
            placeholder="Optional — anything you want us to see"
            className="w-full text-[12.5px] text-slate-700 bg-white/80 border border-amber-200/60 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none leading-snug"
            data-testid={`feedback-reason-input-${messageId}`}
          />
          <div className="flex items-center justify-end gap-2 mt-1.5">
            <button
              type="button"
              onClick={() => setShowReason(false)}
              className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1"
              data-testid={`feedback-reason-dismiss-${messageId}`}
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={submitReason}
              disabled={submitting}
              className="text-[11px] font-medium text-white bg-amber-700 hover:bg-amber-800 rounded px-3 py-1 disabled:opacity-60"
              data-testid={`feedback-reason-submit-${messageId}`}
            >
              Send
            </button>
          </div>
        </div>
      )}
      {reasonSent && (
        <p className="mt-1 text-[10.5px] text-slate-500">Thanks — we'll look at this.</p>
      )}
    </div>
  );
};

function formatMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li class="ml-3 list-disc">$1</li>')
    .replace(/\n/g, '<br/>');
}

// Parse the optional reasoning footer the LLM may emit at the end of substantive replies.
// Format: §§{"most_likely":"...","also_possible":"...","key_question":"...","next_step":"..."}§§
// Returns { body, chips } where `body` is the message without the footer.
function parseReasoningFooter(content) {
  if (!content) return { body: content, chips: null };
  // Match the footer anywhere near the end (be forgiving about trailing whitespace/newlines).
  const m = content.match(/\s*§§(\{[\s\S]*?\})§§\s*$/);
  if (!m) return { body: content, chips: null };
  try {
    const parsed = JSON.parse(m[1]);
    const body = content.slice(0, m.index).trimEnd();
    // Only show chips if we got at least two meaningful fields
    const filled = ['most_likely', 'also_possible', 'key_question', 'next_step']
      .filter(k => parsed[k] && typeof parsed[k] === 'string' && parsed[k].trim().length > 0);
    if (filled.length < 2) return { body, chips: null };
    return { body, chips: parsed };
  } catch {
    return { body: content, chips: null };
  }
}

const CHIP_CONFIG = [
  { key: 'most_likely',    label: 'Most likely',       className: 'bg-[#235347]/8 text-[#1c4439] border-[#235347]/15' },
  { key: 'also_possible',  label: 'Also possible',     className: 'bg-slate-50 text-slate-700 border-slate-200' },
  { key: 'key_question',   label: 'Key to clarify',    className: 'bg-amber-50/70 text-amber-800 border-amber-200/60' },
  // Stage 17 P5 — softened from "Next step" (reads as instruction) to
  // "Worth discussing with your doctor" (reads as conversation starter).
  { key: 'next_step',      label: 'Worth discussing with your doctor', className: 'bg-[#8eb69b]/10 text-[#1c4439] border-[#8eb69b]/25' },
];

const ReasoningChips = ({ chips }) => {
  if (!chips) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="reasoning-chips">
      {CHIP_CONFIG.map(({ key, label, className }) => {
        const value = chips[key];
        if (!value || typeof value !== 'string' || !value.trim()) return null;
        return (
          <div
            key={key}
            className={`text-xs leading-snug border rounded-lg px-2.5 py-1.5 ${className}`}
            data-testid={`reasoning-chip-${key}`}
          >
            <span className="font-semibold tracking-wide uppercase text-[9px] opacity-70 mr-1.5">{label}</span>
            <span>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Fallback Suggestions ─── */
const FALLBACK_SUGGESTIONS = [
  'I have symptoms I want to understand better',
  'Help me prepare for my next appointment',
  'What should I know about my medications?',
  'I have a blood test — what do these results mean?',
];

/* ─── Prompt Card with expandable reason ─── */
const PromptCard = ({ prompt, index, Icon, onTap }) => {
  const [expanded, setExpanded] = useState(false);
  const hasReason = prompt.reason && prompt.reason.length > 0;

  return (
    <div className="bg-white border border-slate-100 rounded-xl overflow-hidden" data-testid={`chat-suggestion-${index}`}>
      <button
        onClick={() => onTap(prompt.text)}
        className="w-full flex items-start gap-2.5 text-left px-3.5 py-3 hover:bg-slate-50 transition-colors min-h-[44px]"
      >
        <Icon size={13} className="text-slate-400 flex-shrink-0 mt-0.5" weight="duotone" />
        <span className="text-[13px] text-slate-600 leading-snug flex-1">{prompt.text}</span>
      </button>
      {hasReason && (
        <div className="px-3.5 pb-2.5">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-500 transition-colors"
            data-testid={`prompt-why-toggle-${index}`}
          >
            <CaretDown size={10} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            Why this is showing
          </button>
          {expanded && (
            <p className="text-[11px] text-slate-400 mt-1 leading-relaxed pl-3.5" data-testid={`prompt-why-text-${index}`}>
              {prompt.reason}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

/* ─── Primary Action Card ─── */
const PrimaryActionCard = ({ activeCourses, safetySignals, planItems, onAction }) => {
  const inProgressCourse = (activeCourses || []).find(c => c.status !== 'completed');
  const highSignal = (safetySignals || []).find(s => (s.severity || s.confidence) === 'high');
  const topPlan = (planItems || [])[0];

  if (inProgressCourse) {
    const total = (inProgressCourse.sections || []).length;
    const step = inProgressCourse.current_step || 0;
    return (
      <div className="bg-[#235347]/5 border border-[#235347]/12 rounded-xl p-4" data-testid="chat-action-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Notebook size={14} className="text-[#235347]" weight="duotone" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#235347]">Continue your learning</span>
        </div>
        <p className="text-sm text-slate-700 leading-snug mb-1 line-clamp-1">{inProgressCourse.title}</p>
        <p className="text-[11px] text-slate-400 mb-3">Step {step + 1} of {total}</p>
        <button onClick={() => onAction('course', inProgressCourse.id)}
          className="inline-flex items-center gap-1.5 bg-[#235347] text-white rounded-lg px-4 py-2.5 text-xs font-medium hover:bg-[#1A3F36] transition-colors min-h-[40px]"
          data-testid="chat-action-cta">
          <Notebook size={14} weight="bold" /> Continue
        </button>
      </div>
    );
  }

  if (highSignal) {
    return (
      <div className="bg-red-50/60 border border-red-200/60 rounded-xl p-4" data-testid="chat-action-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Warning size={14} className="text-red-500" weight="duotone" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-red-500">Review something important</span>
        </div>
        <p className="text-sm text-slate-700 leading-snug mb-3 line-clamp-2">{highSignal.message}</p>
        <button onClick={() => onAction('signal')}
          className="inline-flex items-center gap-1.5 bg-red-500 text-white rounded-lg px-4 py-2.5 text-xs font-medium hover:bg-red-600 transition-colors min-h-[40px]"
          data-testid="chat-action-cta">
          <Eye size={14} weight="bold" /> Review now
        </button>
      </div>
    );
  }

  if (topPlan) {
    return (
      <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-xl p-4" data-testid="chat-action-card">
        <div className="flex items-center gap-2 mb-1.5">
          <Target size={14} className="text-emerald-500" weight="duotone" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Your next step</span>
        </div>
        <p className="text-sm text-slate-700 leading-snug mb-3 line-clamp-2">{topPlan.text}</p>
        <button onClick={() => onAction('plan')}
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-4 py-2.5 text-xs font-medium hover:bg-emerald-700 transition-colors min-h-[40px]"
          data-testid="chat-action-cta">
          <ListChecks size={14} weight="bold" /> Start
        </button>
      </div>
    );
  }

  // Default — make it useful
  return (
    <div className="space-y-2.5" data-testid="chat-action-card">
      <button onClick={() => onAction('chat')}
        className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-[#235347]/30 transition-colors group">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#235347]/8 rounded-lg flex items-center justify-center flex-shrink-0">
            <Chat size={16} className="text-[#235347]" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Ask about symptoms, medications, or next steps</p>
            <p className="text-xs text-slate-400 mt-0.5">Get clear answers based on your health context</p>
          </div>
          <CaretRight size={16} className="text-slate-300 group-hover:text-[#235347] transition-colors flex-shrink-0" />
        </div>
      </button>
      <button onClick={() => onAction('upload')}
        className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-blue-300 transition-colors group">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <File size={16} className="text-blue-600" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Upload a health report for review</p>
            <p className="text-xs text-slate-400 mt-0.5">Blood tests, pathology, referrals, scripts</p>
          </div>
          <CaretRight size={16} className="text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
        </div>
      </button>
    </div>
  );
};


/* ─── Main Chat Page ─── */
const ChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [input, setInput] = useState('');
  // Text-paste appointment detection: capture the most recent paste
  // for the AppointmentPasteDetector to evaluate.
  const [pastedText, setPastedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSessions, setShowSessions] = useState(true);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);  // multi-file
  const [confirmingId, setConfirmingId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesTopRef = useRef(null);
  const fileInputRef = useRef(null);
  const prefillHandled = useRef(false);
  const inputRef = useRef(null);
  // Sessions we created this tab — skip auto-fetch on activeSession change because
  // we know they're empty AND we're about to write the user's optimistic echo to state.
  // Without this, fetchMessages races with the tempMsg setMessages and wipes it.
  const inFlightSessionsRef = useRef(new Set());
  // Friction tracking: counts user messages sent in this mounted session.
  const sentCountRef = useRef(0);
  useChatExitNoSend(() => sentCountRef.current);

  // Stage 9 polish (audit P4): cached context flag — does the user have an
  // upcoming appointment? Used to promote the "Prepare this for my doctor"
  // reply chip to a primary navy pill on assistant replies. Loaded once.
  const [hasAppointment, setHasAppointment] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/context-banner`);
        if (!cancelled && data?.has_appointment) setHasAppointment(true);
      } catch { /* graceful */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stage 10 — auto-read toggle persisted in localStorage. When on, every
  // freshly-completed assistant reply auto-plays (sentence-by-sentence via
  // the SpeechSynthesis API). Defaults to off.
  const [autoVoice, setAutoVoice] = useState(() => {
    try { return localStorage.getItem('kindra_auto_voice') === '1'; }
    catch { return false; }
  });
  const toggleAutoVoice = () => {
    setAutoVoice((prev) => {
      const next = !prev;
      try { localStorage.setItem('kindra_auto_voice', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // Context data for action card
  const [activeCourses, setActiveCourses] = useState([]);
  const [safetySignals, setSafetySignals] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const [dailyPrompts, setDailyPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);

  // ── Welcome (activation) state ────────────────────────────────
  // When the user lands on /chat?welcome=1 immediately after onboarding, we run
  // a Kindra-led opening: instant intro line + streamed reflection + 3 tailored
  // prompts. Upload prompt is held back until after first interaction.
  const [welcomePrompts, setWelcomePrompts] = useState(null);    // null = regular chat; [] = active welcome (no prompts yet)
  const [starterMode, setStarterMode] = useState(null);          // Stage-13 — 'personalised' | 'examples' | null
  const [welcomeStreaming, setWelcomeStreaming] = useState(false);
  const [welcomeShowUpload, setWelcomeShowUpload] = useState(false);
  const welcomeStartedRef = useRef(false);
  const welcomeMountAtRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/sessions`);
      setSessions(data);
      // P1.3 — resume latest session on /chat mount when:
      //   • no activeSession is already set (no deep link, no prior selection)
      //   • most recent session had activity in the last 24h
      //   • we're not in welcome-mode (which has its own scaffolding)
      try {
        const latest = Array.isArray(data) ? data[0] : null;
        if (latest && latest.id && !activeSession && !welcomePrompts) {
          const lastActivity = latest.last_activity ? new Date(latest.last_activity) : null;
          const ageMs = lastActivity ? (Date.now() - lastActivity.getTime()) : Infinity;
          // 24h freshness window — past that, give the user a clean canvas.
          if (ageMs < 24 * 3600 * 1000 && !latest.is_welcome) {
            setActiveSession(latest.id);
          }
        }
      } catch { /* best-effort resume; never block session list */ }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeSession, welcomePrompts]);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const { data } = await axios.get(`${API}/chat/sessions/${sessionId}/messages?limit=30`);
      // Handle both old (array) and new (object with messages + has_older) response formats
      if (Array.isArray(data)) {
        setMessages(data);
        setHasOlderMessages(false);
      } else {
        setMessages(data.messages || []);
        setHasOlderMessages(data.has_older || false);
      }
      setTimeout(scrollToBottom, 100);
    } catch (err) { console.error(err); }
  }, [scrollToBottom]);

  const loadOlderMessages = useCallback(async () => {
    if (!activeSession || loadingOlder || !hasOlderMessages || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const oldestTimestamp = messages[0]?.created_at;
      const { data } = await axios.get(`${API}/chat/sessions/${activeSession}/messages?limit=30&before=${oldestTimestamp}`);
      const result = Array.isArray(data) ? { messages: data, has_older: false } : data;
      if (result.messages?.length) {
        setMessages(prev => [...result.messages, ...prev]);
        setHasOlderMessages(result.has_older || false);
      } else {
        setHasOlderMessages(false);
      }
    } catch (err) { console.error(err); }
    finally { setLoadingOlder(false); }
  }, [activeSession, loadingOlder, hasOlderMessages, messages]);

  // Fetch context data (courses, signals, plan)
  const fetchContext = useCallback(async () => {
    try {
      const [coursesRes, recordRes, planRes] = await Promise.allSettled([
        axios.get(`${API}/university/courses`),
        axios.get(`${API}/health/record`),
        axios.get(`${API}/university/plan`),
      ]);
      if (coursesRes.status === 'fulfilled') setActiveCourses((coursesRes.value.data || []).filter(c => c.status !== 'completed'));
      if (recordRes.status === 'fulfilled') setSafetySignals(recordRes.value.data?.safety_signals || []);
      if (planRes.status === 'fulfilled') setPlanItems((planRes.value.data || []).filter(i => !i.done));
    } catch { /* ignore */ }
  }, []);

  // Fetch daily prompts (async, non-blocking)
  const fetchPrompts = useCallback(async () => {
    setPromptsLoading(true);
    try {
      const { data } = await axios.get(`${API}/university/daily-prompts`);
      if (data.prompts?.length > 0) setDailyPrompts(data.prompts);
    } catch { /* fallback handled in render */ }
    finally { setPromptsLoading(false); }
  }, []);

  useEffect(() => {
    fetchSessions();  // Load sessions immediately
    // Defer non-critical context to avoid blocking initial render
    const timer = setTimeout(() => { fetchContext(); fetchPrompts(); }, 300);
    return () => clearTimeout(timer);
  }, [fetchSessions, fetchContext, fetchPrompts]);

  // Wave 1 fix sprint (2026-02): on /chat landing view (no active session,
  // showSessions=true), focus the composer input on mount on desktop only —
  // mobile is left alone so the keyboard doesn't pop up unexpectedly.
  // This addresses the audit finding "user lands on session list, not ready
  // to type" without changing the layout or auto-opening a stale session.
  useEffect(() => {
    if (activeSession || !showSessions) return;
    if (loading) return;
    const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
    if (!isDesktop) return;
    const t = setTimeout(() => { inputRef.current?.focus(); }, 250);
    return () => clearTimeout(t);
  }, [activeSession, showSessions, loading]);

  useEffect(() => {
    if (location.state?.prefillMessage && !prefillHandled.current) {
      prefillHandled.current = true;
      setInput(location.state.prefillMessage);
      window.history.replaceState({}, document.title);
    }
    // Phase 7: activation handoff deep link — auto-populate the input so the user's
    // first chat moment is zero-friction.
    // Stage 20B — also accept ?prompt= so deep-links from the report detail page
    // (e.g. "Ask Kindra how these connect", "Ask Kindra when to re-test") seed
    // the composer instantly. Same one-shot guard as ?seed=.
    try {
      const url = new URL(window.location.href);
      const seed = url.searchParams.get('seed') || url.searchParams.get('prompt');
      if (seed && !prefillHandled.current) {
        prefillHandled.current = true;
        setInput(seed);
      }
    } catch { /* no-op */ }
  }, [location.state]);

  useEffect(() => {
    if (activeSession) {
      // Skip auto-fetch for sessions we just created in handleSend — we're about to
      // write optimistic state for the message that spawned the session.
      if (inFlightSessionsRef.current.has(activeSession)) {
        setShowSessions(false);
        return;
      }
      fetchMessages(activeSession);
      setShowSessions(false);
    }
  }, [activeSession, fetchMessages]);

  // ── Welcome (activation) stream ─────────────────────────────────
  // Triggered by `?welcome=1` on mount. Streams an immediate intro line, then
  // a Kindra-led reflection grounded in onboarding data, then 3 tailored prompts.
  // After first user→Kindra exchange, an upload prompt is revealed.
  // NOTE: `welcomeStartedRef` is the only re-entry guard (StrictMode double-mount safe).
  // We do NOT cancel the in-flight stream on unmount — React will silently no-op
  // state updates from an unmounted component, and we want the stream to finish so
  // the welcome session is persisted in the DB even if the user navigates away.
  useEffect(() => {
    const run = async () => {
      if (welcomeStartedRef.current) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('welcome') !== '1') return;
      welcomeStartedRef.current = true;
      welcomeMountAtRef.current = Date.now();

      // Track activation moment
      try {
        track('activation_welcome_chat_opened', {});
      } catch { /* noop */ }

      setShowSessions(false);
      setWelcomePrompts([]);            // mark active welcome
      setWelcomeStreaming(true);

      // Insert an empty placeholder assistant message we'll grow as tokens arrive.
      const streamMsgId = `welcome_${Date.now()}`;
      setMessages([{
        id: streamMsgId,
        role: 'assistant',
        content: '',
        streaming: true,
        is_welcome: true,
        created_at: new Date().toISOString(),
      }]);

      let assembled = '';
      let sid = null;
      let firstChunkSeen = false;

      // Sprint Stage-1: client-side strand-protection — if NO 'chunk' event
      // arrives within 8s of the response opening, we abandon and show a
      // compact "I'm here when you're ready" fallback so the user never sits
      // on the intro line indefinitely.
      let firstChunkTimer = null;
      const armFirstChunkTimer = () => {
        if (firstChunkTimer) clearTimeout(firstChunkTimer);
        firstChunkTimer = setTimeout(() => {
          if (!firstChunkSeen) {
            const fallback =
              ' I’m here whenever you want to start — try one of the prompts below, or just type what’s on your mind.';
            assembled += fallback;
            setMessages(prev => prev.map(m => m.id === streamMsgId ? { ...m, content: assembled } : m));
            setWelcomeStreaming(false);
          }
        }, 8000);
      };
      const clearFirstChunkTimer = () => {
        if (firstChunkTimer) { clearTimeout(firstChunkTimer); firstChunkTimer = null; }
      };

      try {
        const { API: apiBase } = await import('../services/api');
        const resp = await fetch(`${apiBase}/chat/welcome`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Accept': 'text/event-stream' },
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`welcome stream failed (${resp.status})`);
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Process SSE — events look like `data: {...}\n\n`
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          for (const ev of events) {
            const dataLine = ev.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            const payload = dataLine.slice(6).trim();
            if (!payload) continue;
            let parsed;
            try { parsed = JSON.parse(payload); } catch { continue; }
            if (parsed.type === 'session') {
              sid = parsed.session_id;
              if (sid) {
                inFlightSessionsRef.current.add(sid);
                setActiveSession(sid);
              }
            } else if (parsed.type === 'intro') {
              // Static intro line — appears IMMEDIATELY (option C). On replay this
              // contains the FULL message; on first run it's just the opening line.
              assembled = parsed.text || '';
              setMessages(prev => prev.map(m => m.id === streamMsgId ? { ...m, content: assembled } : m));
              // Sprint Stage-1: arm the strand-protection timer the moment we see
              // the intro — so we know the stream is alive even if the LLM hangs.
              armFirstChunkTimer();
            } else if (parsed.type === 'chunk') {
              if (!firstChunkSeen) {
                // Insert paragraph break BETWEEN the static intro line and the
                // streamed reflection — once.
                if (assembled && !assembled.endsWith('\n\n')) assembled += '\n\n';
                firstChunkSeen = true;
                clearFirstChunkTimer();
              }
              assembled += parsed.token || '';
              setMessages(prev => prev.map(m => m.id === streamMsgId ? { ...m, content: assembled } : m));
            } else if (parsed.type === 'prompts') {
              setWelcomePrompts(parsed.prompts || []);
            } else if (parsed.type === 'done') {
              clearFirstChunkTimer();
              setMessages(prev => prev.map(m => m.id === streamMsgId
                ? { ...m, id: parsed.message_id || m.id, streaming: false, autoVoiceEligible: true }
                : m));
              setWelcomeStreaming(false);
              if (sid) {
                setActiveSession(sid);
              }
            }
          }
        }
      } catch (e) {
        console.error('[welcome] stream error', e);
        setMessages(prev => prev.map(m => m.id === streamMsgId
          ? { ...m, streaming: false, autoVoiceEligible: true, content: assembled || 'Welcome — let’s start a conversation. Tell me what brings you in today.' }
          : m));
        setWelcomeStreaming(false);
      } finally {
        // Strip ?welcome=1 so a refresh doesn't re-fire (the session persists in DB).
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete('welcome');
          window.history.replaceState({}, '', u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : ''));
        } catch { /* noop */ }
      }
    };
    run();
    // No-op cleanup: do NOT cancel — let the stream complete so the welcome
    // session persists even if the user navigates away mid-stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track session duration for the welcome moment — fires on unmount or page hide.
  useEffect(() => {
    return () => {
      if (welcomeMountAtRef.current) {
        const seconds = Math.round((Date.now() - welcomeMountAtRef.current) / 1000);
        try {
          track('welcome_session_duration', {
            seconds,
            sent_messages: sentCountRef.current,
            saw_upload_prompt: !!welcomeShowUpload,
          });
        } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleNewSession = async (retryCount = 0) => {
    try {
      console.log('[Kindra] Creating new chat session...');
      const { data } = await axios.post(`${API}/chat/sessions`);
      console.log('[Kindra] Session response:', { id: data?.id, title: data?.title, keys: Object.keys(data || {}) });

      if (!data?.id) {
        console.error('[Kindra] Session response missing id:', data);
        if (retryCount < 1) {
          console.log('[Kindra] Retrying session creation...');
          return handleNewSession(retryCount + 1);
        }
        toast.error('Unable to start conversation — please try again');
        return;
      }

      setSessions(prev => [data, ...prev]);
      setActiveSession(data.id);
      setMessages([]); setHasOlderMessages(false);
      setShowSessions(false);
      console.log('[Kindra] Session active:', data.id);
      // Activation funnel: first chat session is a critical milestone.
      trackOnce(ACTIVATION_EVENTS.FIRST_CHAT_STARTED);
    } catch (err) {
      console.error('[Kindra] Session creation failed:', err?.response?.status, err?.message);
      if (retryCount < 1) {
        console.log('[Kindra] Retrying after error...');
        return handleNewSession(retryCount + 1);
      }
      toast.error('Failed to create conversation — please try again', {
        action: { label: 'Retry', onClick: () => handleNewSession(0) },
      });
    }
  };

  const handleDeleteSession = async (id) => {
    try {
      await axios.delete(`${API}/chat/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeSession === id) { setActiveSession(null); setMessages([]); setHasOlderMessages(false); setShowSessions(true); }
      toast('Conversation deleted');
    } catch { toast.error('Failed to delete conversation'); }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text && !pendingFile && pendingFiles.length === 0) return;
    if (sending) return;

    // Friction tracking: count this as a real user-initiated send.
    sentCountRef.current = (sentCountRef.current || 0) + 1;

    let sid = activeSession;
    if (!sid) {
      try {
        const { data } = await axios.post(`${API}/chat/sessions`);
        if (!data?.id) { toast.error('Unable to start conversation'); return; }
        setSessions(prev => [data, ...prev]);
        sid = data.id;
        // Mark this session as in-flight so the auto-fetch useEffect doesn't race
        // with the tempMsg we're about to add (which would wipe the optimistic echo).
        inFlightSessionsRef.current.add(sid);
        setActiveSession(sid);
        // Activation funnel: first chat starts via the Send button path.
        trackOnce(ACTIVATION_EVENTS.FIRST_CHAT_STARTED);
        // Phase 7: if we arrived via the activation handoff, mark the first-chat milestone.
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.get('first') === '1') {
            axios.post(`${API}/activation/track`, { event: 'first_chat_completed', props: { source: 'activation_handoff' } }).catch(() => {});
            // Strip the query params so a page refresh doesn't double-fire.
            url.searchParams.delete('first');
            url.searchParams.delete('seed');
            window.history.replaceState({}, '', url.pathname);
          }
        } catch { /* best effort */ }
      } catch (err) {
        toast.error('Failed to start conversation');
        return;
      }
    }

    // Multi-file report upload
    if (pendingFiles.length > 1) {
      const fileNames = pendingFiles.map(f => f.name).join(', ');
      const tempMsg = {
        id: 'temp-' + Date.now(), role: 'user',
        content: `[Report: ${pendingFiles.length} files — ${fileNames}]${text ? `\n${text}` : ''}`,
        report: { files: pendingFiles.map(f => ({ filename: f.name })) },
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, tempMsg]);
      setInput('');
      setSending(true);
      setPendingFiles([]);
      setShowSessions(false);
      setTimeout(scrollToBottom, 100);

      try {
        const formData = new FormData();
        formData.append('session_id', sid);
        if (text) formData.append('message', text);
        pendingFiles.forEach(f => formData.append('files', f));

        const { data } = await axios.post(`${API}/chat/upload-report`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        // Add processing indicator
        setMessages(prev => [
          ...prev.filter(m => m.id !== tempMsg.id),
          { ...tempMsg, id: data.user_message_id || tempMsg.id },
          { id: data.assistant_message_id, role: 'assistant', content: `Reviewing ${pendingFiles.length} files...`, report_processing: { status: 'processing' }, created_at: new Date().toISOString() },
        ]);
        // Safe to release in-flight flag now that the real user_message_id is written to state
        inFlightSessionsRef.current.delete(sid);
        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            await fetchMessages(sid);
            const latest = await axios.get(`${API}/chat/sessions/${sid}/messages?limit=5`);
            const msgs = Array.isArray(latest.data) ? latest.data : (latest.data.messages || []);
            const reportMsg = msgs.find(m => m.id === data.assistant_message_id);
            if (reportMsg && reportMsg.report_processing?.status !== 'processing') {
              clearInterval(pollInterval);
              await fetchMessages(sid);
              await fetchSessions();
            }
          } catch { clearInterval(pollInterval); }
        }, 3000);
        // Safety timeout
        setTimeout(() => { clearInterval(pollInterval); fetchMessages(sid); }, 60000);
      } catch (err) {
        toast.error('Failed to upload report');
        inFlightSessionsRef.current.delete(sid);
        setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      } finally { setSending(false); }
      return;
    }

    const tempMsg = {
      id: 'temp-' + Date.now(), role: 'user',
      content: pendingFile ? `[Attached: ${pendingFile.name}]\n${text}` : text,
      // Wave 3 fix sprint (2026-02): also stash the file's MIME type, size,
      // and an objectURL so the message bubble can render a real preview
      // (image thumbnail or document card) instead of just the filename.
      // The objectURL is only meaningful for the temp/optimistic message —
      // when the server's persisted message comes back via fetchMessages, it
      // won't have an objectURL and falls back to the filename card. That's
      // fine: the user has already seen the upload visually confirmed.
      attachment: pendingFile ? {
        filename: pendingFile.name,
        type: pendingFile.type,
        size: pendingFile.size,
        previewUrl: pendingFile.type?.startsWith('image/') ? URL.createObjectURL(pendingFile) : null,
      } : null,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMsg]);
    setInput('');
    setSending(true);
    setTimeout(scrollToBottom, 100);

    // File uploads use the non-streaming endpoint
    if (pendingFile) {
      const formData = new FormData();
      formData.append('session_id', sid);
      if (text) formData.append('message', text);
      formData.append('file', pendingFile);
      setPendingFile(null);
      try {
        await axios.post(`${API}/chat`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        inFlightSessionsRef.current.delete(sid);
        setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
        await fetchMessages(sid);
        await fetchSessions();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to send message');
        inFlightSessionsRef.current.delete(sid);
        setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      } finally { setSending(false); }
      return;
    }

    // Text messages use SSE streaming
    setPendingFile(null);
    const streamMsgId = 'stream-' + Date.now();
    setMessages(prev => [...prev, { id: streamMsgId, role: 'assistant', content: '', streaming: true, created_at: new Date().toISOString() }]);
    setTimeout(scrollToBottom, 100);

    // rAF-throttled token renderer — collapses many setMessages calls into ~60fps batches.
    // This keeps the thread smooth and eliminates the per-token React churn that made
    // responses feel laggy.
    let accumulated = '';
    let pendingRaf = null;
    const flushToUI = () => {
      pendingRaf = null;
      setMessages(prev => prev.map(m => m.id === streamMsgId ? { ...m, content: accumulated } : m));
    };
    const scheduleFlush = () => {
      if (pendingRaf) return;
      pendingRaf = (window.requestAnimationFrame || ((cb) => setTimeout(cb, 16)))(flushToUI);
    };

    try {
      const formData = new FormData();
      formData.append('session_id', sid);
      formData.append('message', text);

      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamEndedCleanly = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const payload = JSON.parse(line.slice(6));

            if (payload.token) {
              accumulated += payload.token;
              scheduleFlush();
            }

            if (payload.using_context) {
              setMessages(prev => prev.map(m => m.id === streamMsgId
                ? { ...m, using_context: payload.using_context }
                : m));
            }

            if (payload.done) {
              streamEndedCleanly = true;
              // Flush any pending tokens before reconciling
              if (pendingRaf) { cancelAnimationFrame(pendingRaf); pendingRaf = null; flushToUI(); }
              // CRITICAL: do NOT remove tempMsg — user's message stays visible while we reconcile.
              // Promote the streaming placeholder to the real assistant message ID from the server,
              // so fetchMessages can dedupe by ID without the UI flickering.
              setMessages(prev => prev.map(m => {
                if (m.id === streamMsgId) return { ...m, id: payload.message_id || streamMsgId, streaming: false };
                if (m.id === tempMsg.id) return { ...m, persisted: true };
                return m;
              }));
              // Release the in-flight flag now that the optimistic messages are finalised —
              // a future visit to this session will re-fetch the authoritative DB state.
              inFlightSessionsRef.current.delete(sid);
              // Background reconcile (fire-and-forget) — replaces our optimistic state with the
              // authoritative DB state, but the user never sees an empty thread.
              fetchMessages(sid).catch(() => {});
              fetchSessions().catch(() => {});
              // Welcome flow: trust is now established — reveal the upload prompt
              // and fire the first-message tracking event.
              if (welcomePrompts && welcomePrompts.length > 0 && !welcomeShowUpload) {
                setWelcomeShowUpload(true);
                try {
                  track('welcome_first_message_sent', {
                    seconds_since_open: welcomeMountAtRef.current
                      ? Math.round((Date.now() - welcomeMountAtRef.current) / 1000)
                      : null,
                  });
                } catch { /* noop */ }
              }
            }

            if (payload.error) {
              throw new Error(payload.error);
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
          }
        }
      }

      // Stream ended without a 'done' event (timeout/disconnect). Keep what we have and reconcile.
      if (!streamEndedCleanly) {
        if (pendingRaf) { cancelAnimationFrame(pendingRaf); pendingRaf = null; flushToUI(); }
        setMessages(prev => prev.map(m => m.id === streamMsgId ? { ...m, streaming: false } : m));
        inFlightSessionsRef.current.delete(sid);
        fetchMessages(sid).catch(() => {});
      }
    } catch (err) {
      console.error('[Kindra] Stream error:', err);
      if (pendingRaf) { cancelAnimationFrame(pendingRaf); pendingRaf = null; }
      inFlightSessionsRef.current.delete(sid);
      // Remove ONLY the assistant streaming placeholder — keep the user's message visible.
      setMessages(prev => prev.filter(m => m.id !== streamMsgId));
      try {
        const formData = new FormData();
        formData.append('session_id', sid);
        formData.append('message', text);
        await axios.post(`${API}/chat`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        // Now reconcile — DB has the user message, so temp can go away safely
        await fetchMessages(sid);
        await fetchSessions();
      } catch (fallbackErr) {
        // Fallback also failed — restore the draft so the user doesn't lose their text
        setInput(text);
        setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
        toast.error(fallbackErr.response?.data?.detail || 'Failed to send. Your message is restored.');
      }
    } finally { setSending(false); }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    const valid = selectedFiles.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is too large. Maximum 10MB.`); return false; }
      return true;
    });
    if (valid.length === 1) {
      setPendingFile(valid[0]);
      setPendingFiles([]);
    } else if (valid.length > 1) {
      setPendingFile(null);
      setPendingFiles(valid);
    }
    e.target.value = '';

    // Stage 4 — when called from the landing view (no active session), create
    // a session immediately so the pending file becomes visible in the active
    // chat view. The existing optimistic-echo guard prevents the auto-fetch race.
    (async () => {
      if (activeSession) return;
      if (valid.length === 0) return;
      try {
        const { data } = await axios.post(`${API}/chat/sessions`);
        if (data?.id) {
          inFlightSessionsRef.current.add(data.id);
          setSessions(prev => [data, ...prev]);
          setActiveSession(data.id);
          setShowSessions(false);
        }
      } catch { /* best-effort; user can still tap send to retry */ }
    })();
  };

  const handleConfirmFact = async (pendingId) => {
    setConfirmingId(pendingId);
    try {
      const { data } = await axios.post(`${API}/chat/confirm-fact`, { pending_id: pendingId, action: 'confirm' });
      setMessages(prev => prev.map(m => ({
        ...m,
        health_actions: m.health_actions ? {
          ...m.health_actions,
          pending_confirmation: (m.health_actions.pending_confirmation || []).filter(p => p.id !== pendingId),
        } : m.health_actions,
      })));
      const result = data?.result;
      if (result?.type === 'appointment' && result?.id) {
        toast.success('Appointment saved', {
          description: result.title || 'Added to your appointments',
          action: {
            label: 'Add to Calendar',
            onClick: () => {
              const link = document.createElement('a');
              link.href = `${API}/appointments/${result.id}/ics`;
              link.download = 'appointment.ics';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            },
          },
        });
      } else { toast.success('Saved to your health record'); }
    } catch { toast.error('Failed to save'); }
    finally { setConfirmingId(null); }
  };

  const handleDismissFact = async (pendingId) => {
    setConfirmingId(pendingId);
    try {
      await axios.post(`${API}/chat/confirm-fact`, { pending_id: pendingId, action: 'dismiss' });
      setMessages(prev => prev.map(m => ({
        ...m,
        health_actions: m.health_actions ? {
          ...m.health_actions,
          pending_confirmation: (m.health_actions.pending_confirmation || []).filter(p => p.id !== pendingId),
        } : m.health_actions,
      })));
      toast('Dismissed');
    } catch { toast.error('Failed to dismiss'); }
    finally { setConfirmingId(null); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleActionCard = (action, courseId) => {
    if (action === 'course') navigate('/learn', { state: { resumeCourseId: courseId } });
    else if (action === 'signal') navigate('/health');
    else if (action === 'plan') navigate('/learn');
    else if (action === 'upload') { fileInputRef.current?.click(); }
    else handleNewSession();
  };

  const handleSuggestion = (text) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const greeting = getGreeting();
  const hasContext = activeCourses.length > 0 || safetySignals.length > 0 || planItems.length > 0;
  const contextLine = activeCourses.length > 0
    ? 'Continue your learning'
    : safetySignals.some(s => (s.severity || s.confidence) === 'high')
      ? '1 thing to review today'
      : planItems.length > 0
        ? `${planItems.length} step${planItems.length !== 1 ? 's' : ''} in your plan`
        : sessions.length > 0
          ? 'Ask anything or upload a report'
          : 'Your health companion is ready';

  // ─── Active conversation view ───
  if (activeSession && !showSessions) {
    return (
      <MainLayout noPadding hideFooter>
        <div className="lg:flex lg:flex-row lg:max-w-[1440px] lg:mx-auto lg:gap-0">
          {/* DESKTOP-ONLY left rail — persistent session list at lg+.
              On mobile this stays hidden (the existing fullscreen sessions
              view at the bottom of this file owns the mobile UX). */}
          <aside
            className="hidden lg:flex lg:flex-col lg:w-[300px] lg:flex-shrink-0 lg:border-r lg:border-[rgba(14,28,46,0.08)] lg:bg-white lg:py-6"
            style={{ height: 'calc(100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }}
            data-testid="chat-desktop-rail"
          >
            <div className="px-5 pb-4">
              <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium">Conversations</p>
              <button
                onClick={handleNewSession}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0E1C2E] text-white text-[13px] font-medium rounded-lg px-3 py-2.5 hover:bg-[#1A2A3F] transition-colors"
                data-testid="chat-rail-new"
              >
                + New conversation
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3">
              {sessions.map((s) => {
                const isActive = s.id === activeSession;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSession(s.id)}
                    className={`w-full text-left rounded-lg px-3 py-2.5 mb-1 transition-colors ${
                      isActive
                        ? 'bg-[#FAF7EF] border border-[#E6DEC9]'
                        : 'hover:bg-[#FBFAF6] border border-transparent'
                    }`}
                    data-testid={`chat-rail-session-${s.id}`}
                  >
                    <p className="text-[13.5px] text-[#0E1C2E] truncate leading-snug">{s.title || 'New conversation'}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                      {s.last_activity ? new Date(s.last_activity).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                    </p>
                  </button>
                );
              })}
              {sessions.length === 0 && (
                <p className="text-[12px] text-slate-400 px-3 py-2 italic leading-relaxed">
                  Your past conversations will appear here.
                </p>
              )}
            </div>
          </aside>

          {/* MAIN thread column. On mobile this is the only column. */}
          <div className="flex flex-col w-full max-w-full lg:max-w-[820px] lg:mx-auto overflow-x-hidden" style={{ height: 'calc(100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }} data-testid="chat-page">
          <header className="glass-header px-4 py-3 flex items-center gap-3 border-b border-slate-100 flex-shrink-0 min-h-[48px] safe-top">
            <button onClick={() => setShowSessions(true)} className="lg:hidden text-slate-400 hover:text-slate-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="back-to-sessions">
              <CaretLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-sm font-semibold text-[#0F172A] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {sessions.find(s => s.id === activeSession)?.title || 'Chat'}
              </h1>
            </div>
            {/* Stage 10 — voice mode toggle in active-session header */}
            <button
              onClick={toggleAutoVoice}
              type="button"
              data-testid="chat-voice-toggle"
              aria-label={autoVoice ? 'Voice mode on' : 'Voice mode off'}
              title={autoVoice ? 'Voice mode on — Kindra reads replies aloud' : 'Tap to have Kindra read replies aloud'}
              className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 rounded-full transition-colors flex-shrink-0 ${
                autoVoice
                  ? 'bg-[#2FB3A0] text-white'
                  : 'bg-white border border-[#ECE8E1] text-slate-400 hover:text-[#0E1C2E] hover:border-[#CFC8BB]'
              }`}
            >
              {autoVoice ? <SpeakerHigh size={14} weight="duotone" /> : <SpeakerSlash size={14} weight="duotone" />}
            </button>
          </header>

          <div className="flex-1 overflow-hidden">
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto px-4 py-3">
                {/* Collapsed context banner — "I'm caught up on your week." */}
                {messages.length > 0 && (
                  <ChatContextBanner collapsed />
                )}
                {/* Stage 18 P4 — persistent safety disclaimer for active
                    chat sessions. Tiny, italic, slate-400 — present always
                    so the safety framing is visible even deep into a
                    long conversation. */}
                {messages.length > 0 && (
                  <p
                    className="mb-3 text-[11px] text-slate-400 leading-snug italic"
                    data-testid="chat-active-session-disclaimer"
                  >
                    Kindra helps explain and prepare. It doesn't diagnose, prescribe, or replace your clinician.
                  </p>
                )}
                {hasOlderMessages && (
                  <div className="text-center py-2 mb-2">
                    <button
                      onClick={loadOlderMessages}
                      disabled={loadingOlder}
                      data-testid="load-older-messages"
                      className="text-xs text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
                    >
                      {loadingOlder ? 'Loading...' : 'Load earlier messages'}
                    </button>
                  </div>
                )}
                <div ref={messagesTopRef} />
                {messages.length === 0 && !sending && (
                  <div className="text-center py-12">
                    <Chat size={36} className="text-slate-200 mx-auto mb-3" weight="duotone" />
                    <p className="text-sm font-medium text-slate-600">Ask Kindra anything</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-[240px] mx-auto leading-relaxed">Questions, symptoms, medications, or upload documents for analysis</p>
                  </div>
                )}
                <AnimatePresence>
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      onConfirm={handleConfirmFact}
                      onDismiss={handleDismissFact}
                      confirming={confirmingId}
                      sessionId={activeSession}
                      hasAppointmentContext={hasAppointment}
                      autoVoice={autoVoice}
                      onFollowUpFocus={() => inputRef.current?.focus()}
                      onSendFollowup={(text) => { setInput(text); setTimeout(() => handleSend(), 0); }}
                    />
                  ))}
                </AnimatePresence>
                {sending && (
                  <div className="flex justify-start mb-3">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {(pendingFile || pendingFiles.length > 0) && (
                <div className="px-4 pb-1">
                  {pendingFile && (() => {
                    // Wave 3 fix sprint — pre-send tray also previews images.
                    const isImage = (pendingFile.type || '').startsWith('image/');
                    const previewUrl = isImage ? URL.createObjectURL(pendingFile) : null;
                    return (
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="chat-pending-attachment">
                        {isImage && previewUrl ? (
                          <img src={previewUrl} alt="" className="w-10 h-10 rounded-md object-cover bg-white border border-slate-200 flex-shrink-0" />
                        ) : (
                          <File size={14} className="text-slate-400 flex-shrink-0" />
                        )}
                        <span className="text-xs text-slate-600 truncate flex-1">{pendingFile.name}</span>
                        <button
                          onClick={() => setPendingFile(null)}
                          className="text-slate-400 hover:text-red-400 min-w-[32px] min-h-[32px] flex items-center justify-center -mr-1.5 rounded-md"
                          aria-label="Remove attachment"
                        ><X size={14} /></button>
                      </div>
                    );
                  })()}
                  {pendingFiles.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Report: {pendingFiles.length} files</span>
                        <button onClick={() => setPendingFiles([])} className="text-[10px] text-red-400 hover:text-red-500">Clear all</button>
                      </div>
                      {pendingFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                          <File size={12} className="text-slate-400" />
                          <span className="text-xs text-slate-600 truncate flex-1">{f.name}</span>
                          <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400"><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="px-4 pb-3 pt-2 border-t border-slate-100 flex-shrink-0">
                {/* Welcome (activation) prompts — appear once the streaming intro
                    completes. Upload prompt is held back until first interaction. */}
                {welcomePrompts && welcomePrompts.length > 0 && !welcomeStreaming && (
                  <div className="mb-3" data-testid="welcome-prompts">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2 px-0.5">
                      Where shall we begin?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {welcomePrompts.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setInput(p.text);
                            try { track('welcome_prompt_clicked', { id: p.id, kind: p.kind }); } catch {}
                            setTimeout(() => inputRef.current?.focus(), 50);
                          }}
                          className="rounded-full border border-[#ECE8E1] bg-white hover:border-[#CFC8BB] hover:bg-[#FBFAF6] px-3.5 py-2 text-[13px] text-[#0E1C2E] leading-tight transition-colors"
                          data-testid={`welcome-prompt-${p.kind}`}
                        >
                          {p.text}
                        </button>
                      ))}
                      {/* Upload prompt — only appears AFTER first user→Kindra exchange */}
                      {welcomeShowUpload && (
                        <button
                          onClick={() => {
                            try { track('welcome_upload_prompt_clicked', {}); } catch {}
                            navigate('/documents');
                          }}
                          className="rounded-full bg-[#0E1C2E] hover:bg-[#1A2B42] text-white px-3.5 py-2 text-[13px] leading-tight transition-colors"
                          data-testid="welcome-prompt-upload"
                        >
                          Add a recent report so I can read it for you →
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Text-paste appointment detector — surfaces when user pastes SMS/email */}
                <AppointmentPasteDetector
                  pastedText={pastedText}
                  onResolved={() => setPastedText('')}
                />
                <div className="flex items-end gap-2 max-w-full">
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileSelect} className="hidden" multiple />
                  <button onClick={() => fileInputRef.current?.click()}
                    className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-400 hover:text-[#235347] hover:bg-slate-50 rounded-xl transition-colors flex-shrink-0"
                    data-testid="chat-attach-btn" title="Attach document">
                    <Paperclip size={20} />
                  </button>
                  <div className="flex-1 min-w-0 relative">
                    <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                      onPaste={(e) => {
                        const t = e.clipboardData?.getData('text') || '';
                        if (t && looksLikeAppointment(t)) setPastedText(t);
                      }}
                      placeholder={pendingFile || pendingFiles.length > 0 ? "Add a note about these documents..." : "Ask Kindra anything..."}
                      rows={1} className="w-full resize-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-base outline-none focus:ring-2 focus:ring-[#235347]/20 max-h-28 overflow-y-auto"
                      data-testid="chat-input" style={{ minHeight: '44px', fontSize: '16px' }} />
                  </div>
                  {/* Stage 10 — Voice input. Web Speech API; mic hides if unsupported. */}
                  <VoiceInputButton
                    size={44}
                    onResult={(finalText) => {
                      // Append to whatever was already typed.
                      setInput((prev) => (prev ? prev + ' ' : '') + finalText);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    onInterim={(live) => { /* live captions go to a status hint below — wire later if needed */ }}
                    onListeningChange={() => { /* parent doesn't gate the input */ }}
                  />
                  <button onClick={handleSend} disabled={sending || (!input.trim() && !pendingFile && pendingFiles.length === 0)}
                    className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center bg-[#235347] text-white rounded-xl hover:bg-[#1A3F36] transition-colors disabled:opacity-30 flex-shrink-0"
                    data-testid="chat-send-btn">
                    {sending ? <Spinner size={20} className="animate-spin" /> : <PaperPlaneRight size={20} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>{/* /thread column */}
        </div>{/* /lg:flex desktop wrapper */}
      </MainLayout>
    );
  }

  // ─── Home / Landing view (Phase 5B retention rebuild) ───
  return (
    <MainLayout>
      <div className="flex flex-col max-w-full overflow-x-hidden lg:max-w-[820px] lg:mx-auto" style={{ height: 'calc(100dvh - 72px - 32px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }} data-testid="chat-page">

        {/* ZONE 1: Header — greeting + persistent New Chat button */}
        <div className="pt-1 pb-3 flex-shrink-0 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[22px] font-semibold text-[#0E1C2E] tracking-[-0.01em] leading-tight" data-testid="chat-greeting">
              {greeting}
              {firstName ? (
                <>
                  ,{' '}
                  <em
                    className="italic font-medium"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                  >
                    {firstName}
                  </em>
                </>
              ) : ''}
            </p>
            <p className="text-[12.5px] text-slate-500 mt-1 italic leading-relaxed" data-testid="chat-greeting-sub">
              {starterMode === 'examples'
                ? "What can I help with?"
                : "I've caught up on your recent health activity. What's on your mind?"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Stage 10 — Voice mode toggle. Tiny, calm, persists across visits. */}
            <button
              onClick={toggleAutoVoice}
              type="button"
              data-testid="chat-voice-toggle"
              aria-label={autoVoice ? 'Voice mode on — Kindra reads replies aloud' : 'Voice mode off'}
              title={autoVoice ? 'Voice mode on — Kindra reads replies aloud' : 'Voice mode off'}
              className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] w-11 h-11 rounded-full transition-colors ${
                autoVoice
                  ? 'bg-[#2FB3A0] text-white'
                  : 'bg-white border border-[#ECE8E1] text-slate-400 hover:text-[#0E1C2E] hover:border-[#CFC8BB]'
              }`}
            >
              {autoVoice ? <SpeakerHigh size={14} weight="duotone" /> : <SpeakerSlash size={14} weight="duotone" />}
            </button>
            <button
              onClick={handleNewSession}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#0E1C2E] hover:bg-[#1A2B42] text-white text-[13px] font-semibold px-3.5 py-2 min-h-[44px] transition-colors"
              data-testid="chat-new-chat-btn"
            >
              <Plus size={13} weight="bold" />
              New chat
            </button>
          </div>
        </div>

        {/* ZONE 2: Context banner (in-context / welcome-back variants) */}
        <div className="flex-shrink-0">
          <ChatContextBanner />
          {/* Stage 18 P4 — quiet persistent session header disclaimer.
              Sits right below the context banner so it anchors the session
              without interrupting the chat. Hidden tone, present always. */}
          <p
            className="mb-3 text-[11px] text-slate-400 leading-snug italic"
            data-testid="chat-session-disclaimer"
          >
            Kindra helps explain and prepare. It doesn't diagnose, prescribe, or replace your clinician.
          </p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="space-y-5">

            {/* ZONE 3: Quick input with trust signal */}
            <div data-testid="chat-quick-start">
              <AppointmentPasteDetector
                pastedText={pastedText}
                onResolved={() => setPastedText('')}
              />
              <div className="bg-white border border-[#ECE8E1] rounded-xl overflow-hidden focus-within:border-[#CFC8BB] transition-colors">
                <div className="flex items-center gap-2 px-4 py-3">
                  <MagnifyingGlass size={14} className="text-slate-300 flex-shrink-0" />
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    onPaste={(e) => {
                      const t = e.clipboardData?.getData('text') || '';
                      if (t && looksLikeAppointment(t)) setPastedText(t);
                    }}
                    placeholder="Ask anything about symptoms, reports, medications, patterns…"
                    className="flex-1 bg-transparent text-base outline-none text-slate-700 placeholder:text-slate-400"
                    style={{ fontSize: '16px' }}
                    data-testid="chat-quick-input"
                  />
                  {/* Stage 4 — direct attach from landing; tapping opens the chat
                      composer flow via the existing handleFileSelect (sets
                      pendingFile and navigates into the active session view). */}
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileSelect} className="hidden" multiple />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center text-slate-400 hover:text-[#0E1C2E] hover:bg-slate-50 rounded-lg transition-colors flex-shrink-0"
                    data-testid="chat-quick-attach"
                    title="Attach a document"
                    aria-label="Attach a document"
                  >
                    <Paperclip size={16} />
                  </button>
                  {/* Stage 10 — Voice input on landing composer.
                      Hides if browser doesn't support speech recognition. */}
                  <VoiceInputButton
                    size={36}
                    onResult={(finalText) => {
                      setInput((prev) => (prev ? prev + ' ' : '') + finalText);
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !input.trim()}
                    className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center bg-[#0E1C2E] text-white rounded-lg hover:bg-[#1A2B42] transition-colors disabled:opacity-30 flex-shrink-0"
                    data-testid="chat-quick-send"
                  >
                    {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneRight size={14} />}
                  </button>
                </div>
              </div>
              {/* Trust signal */}
              <p className="mt-2 text-[11px] text-slate-400 leading-relaxed px-0.5" data-testid="chat-trust-signal">
                {starterMode === 'examples'
                  ? "Kindra answers using your reports, appointments and health record when available · kept private, never shared."
                  : "Uses your stored health timeline and recent records · kept private, never shared."}
              </p>
            </div>

            {/* Stage 11 — Voice welcome card (first-chat-only, autoplay once).
                Stage 20B+ Wave B — hidden for any user with existing chat
                sessions; no longer steals real estate on every /chat open. */}
            <VoiceWelcomeCard hasExistingChats={sessions.length > 0} />

            {/* ZONE 4: Hybrid starter prompts (3 fixed + 2 AI-contextual) */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2.5 px-0.5" data-testid="starter-eyebrow">
                {starterMode === 'examples' ? 'Try an example' : 'Start here'}
              </p>
              <ChatStarterPrompts onTap={handleSuggestion} onModeChange={setStarterMode} />
              {starterMode === 'examples' && (
                <p className="mt-2 text-[11px] text-slate-400 leading-relaxed px-0.5" data-testid="starter-examples-microcopy">
                  Examples of what Kindra can help with — nothing about you yet.
                </p>
              )}
            </div>

            {/* ZONE 5: Recent conversations — cleaner treatment */}
            {sessions.length > 0 && (
              <div data-testid="chat-recent-conversations" className="pt-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 px-0.5">
                  Recent conversations
                </p>
                <div className="space-y-1">
                  {sessions.slice(0, 8).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setActiveSession(s.id); }}
                      className="w-full text-left px-3.5 py-3 rounded-xl bg-white border border-[#ECE8E1] hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors cursor-pointer"
                      data-testid="session-item"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[14px] text-[#0E1C2E] truncate flex-1 leading-snug">
                          {s.title || 'Untitled conversation'}
                        </p>
                        <span className="text-[11px] text-slate-400 flex-shrink-0 tabular-nums">
                          {s.last_activity ? (() => {
                            const d = new Date(s.last_activity); const now = new Date();
                            const diff = Math.floor((now - d) / 86400000);
                            return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : diff < 7 ? `${diff}d ago` : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                          })() : ''}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* First-time empty state */}
            {sessions.length === 0 && !loading && (
              <div className="py-4" data-testid="chat-empty-state">
                <p className="text-[13px] text-slate-500 leading-relaxed">
                  Ask anything about symptoms, reports, medications, patterns or what to do next.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ChatPage;
